// src/components/excel-upload/FileItem.tsx
import * as React from "react";
import { motion } from "framer-motion";
import {
  X,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RotateCcw,
  Clock,
} from "lucide-react";
import { cn, formatFileSize, formatTime } from "../../lib/utils";
import { UploadFile } from "./upload.types";

interface FileItemProps {
  file: UploadFile;
  onRemove: () => void;
  onRetry?: () => void;
  disabled?: boolean;
}

export const FileItem: React.FC<FileItemProps> = ({
  file,
  onRemove,
  onRetry,
  disabled = false,
}) => {
  const statusConfig = {
    pending: {
      bg: "bg-white",
      border: "border-gray-200",
      icon: <Clock className="h-4 w-4 text-gray-400" />,
      label: "Ready",
      labelColor: "text-gray-500",
    },
    uploading: {
      bg: "bg-blue-50/50",
      border: "border-blue-200",
      icon: <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />,
      label: `${file.progress}%`,
      labelColor: "text-blue-600",
    },
    success: {
      bg: "bg-emerald-50/50",
      border: "border-emerald-200",
      icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
      label: "Uploaded",
      labelColor: "text-emerald-600",
    },
    error: {
      bg: "bg-red-50/50",
      border: "border-red-200",
      icon: <AlertCircle className="h-4 w-4 text-red-500" />,
      label: "Failed",
      labelColor: "text-red-600",
    },
  };

  const config = statusConfig[file.status];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20, height: 0, marginBottom: 0 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className={cn(
        "group relative flex items-center gap-3 p-3 rounded-xl border transition-all duration-200",
        config.bg,
        config.border,
        "hover:shadow-sm"
      )}
    >
      {/* File Icon with Status Indicator */}
      <div className="relative flex-shrink-0">
        <div className="p-2.5 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl">
          <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
        </div>
        {/* Status dot */}
        <div
          className={cn(
            "absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white flex items-center justify-center",
            file.status === "pending" && "bg-gray-300",
            file.status === "uploading" && "bg-blue-500",
            file.status === "success" && "bg-emerald-500",
            file.status === "error" && "bg-red-500"
          )}
        >
          {file.status === "uploading" && (
            <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
          )}
        </div>
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-800 truncate">
            {file.file.name}
          </p>
        </div>
        
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-gray-400">
            {formatFileSize(file.file.size)}
          </span>
          
          {file.uploadedAt && file.status === "success" && (
            <>
              <span className="text-gray-300">•</span>
              <span className="text-xs text-gray-400">
                {formatTime(file.uploadedAt)}
              </span>
            </>
          )}
          
          {file.status === "error" && file.errorMessage && (
            <>
              <span className="text-gray-300">•</span>
              <span className="text-xs text-red-500 truncate">
                {file.errorMessage}
              </span>
            </>
          )}
        </div>

        {/* Progress Bar */}
        {file.status === "uploading" && (
          <div className="mt-2 h-1 bg-blue-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${file.progress}%` }}
              transition={{ duration: 0.2 }}
            />
          </div>
        )}
      </div>

      {/* Status Badge */}
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "text-xs font-medium px-2 py-1 rounded-lg flex items-center gap-1.5",
            file.status === "pending" && "bg-gray-100 text-gray-600",
            file.status === "uploading" && "bg-blue-100 text-blue-700",
            file.status === "success" && "bg-emerald-100 text-emerald-700",
            file.status === "error" && "bg-red-100 text-red-700"
          )}
        >
          {config.icon}
          {config.label}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {file.status === "error" && onRetry && (
          <button
            onClick={onRetry}
            disabled={disabled}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all disabled:opacity-50"
            title="Retry"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        )}
        {file.status !== "uploading" && (
          <button
            onClick={onRemove}
            disabled={disabled}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50"
            title="Remove"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </motion.div>
  );
};